import { useMemo, useState } from "react";
import { Text } from "../../../../components/common/typography";
import { Input } from "../../../../components/common/input";
import { StyledSelect } from "../../../products/presentation/components/styled-select";
import { useCategories } from "../../../products/hooks/useCategories";
import { useCountries } from "../../../products/hooks/useCountries";
import type { ProductContext } from "./proposed-changes-diff";

/**
 * Inline editor for the `proposedChanges` payload on a CHANGES_REQUESTED CR.
 * Only fields that were in the original proposal are rendered — keeps the
 * resubmit focused on what the admin actually asked the seller to fix. Fields
 * that aren't yet supported for inline edit (images, gallery, lifecycle, etc.)
 * render read-only with a note that the seller would have to cancel and open
 * a new CR to change them.
 */

const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  description: "Description",
  categoryId: "Category",
  countryOfOriginId: "Country of origin",
  variants: "Variants",
  discounts: "Variant discounts",
  archiveVariantIds: "Archived variants",
  compatibleWarrantyIds: "Compatible warranties",
  image: "Main image",
  gallery: "Gallery",
  active: "Active",
  lifecycleStatus: "Lifecycle status",
};

const READ_ONLY_FIELDS = new Set<string>([
  "image",
  "gallery",
  "archiveVariantIds",
  "compatibleWarrantyIds",
  "active",
  "lifecycleStatus",
]);

interface VariantPatch {
  id: number;
  quantity?: number;
  sellerPrice?: number;
  image?: string;
}

interface DiscountPatch {
  variantId: number;
  discountPercentage: number;
}

interface EditableProposedChangesProps {
  proposedChanges: Record<string, unknown>;
  productContext?: ProductContext;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (next: Record<string, unknown>) => Promise<void> | void;
}

function variantLabel(id: number, ctx?: ProductContext): string {
  const v = ctx?.variantsById.get(id);
  if (!v) return `Variant #${id}`;
  return v.sku ? `${v.sku} — ${v.displayName}` : v.displayName;
}

export function EditableProposedChanges({
  proposedChanges,
  productContext,
  isSubmitting,
  onCancel,
  onSubmit,
}: EditableProposedChangesProps) {
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: countries, isLoading: countriesLoading } = useCountries();

  const categoryOptions = useMemo(
    () =>
      (categories ?? []).map((c) => ({
        value: String(c.id),
        label: c.completeName ?? c.displayName ?? c.name,
      })),
    [categories],
  );
  const countryOptions = useMemo(
    () => (countries ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    [countries],
  );

  // Local state seeded from the proposedChanges so the seller starts with
  // exactly what they'd previously submitted.
  const [name, setName] = useState<string>(
    typeof proposedChanges.name === "string" ? proposedChanges.name : "",
  );
  const [description, setDescription] = useState<string>(
    typeof proposedChanges.description === "string" ? proposedChanges.description : "",
  );
  const [categoryId, setCategoryId] = useState<string>(
    proposedChanges.categoryId != null ? String(proposedChanges.categoryId) : "",
  );
  const [countryOfOriginId, setCountryOfOriginId] = useState<string>(
    proposedChanges.countryOfOriginId != null
      ? String(proposedChanges.countryOfOriginId)
      : "",
  );

  // Variants: list of patches from proposedChanges. We track per-field edits so
  // unchanged fields remain absent (instead of getting written back as 0 / "").
  const initialVariants = (proposedChanges.variants ?? []) as VariantPatch[];
  const [variantPatches, setVariantPatches] = useState<VariantPatch[]>(
    initialVariants.map((v) => ({ ...v })),
  );

  // Discounts: paired by variantId. We keep them as-is and let the seller edit
  // their percentage inline.
  const initialDiscounts = (proposedChanges.discounts ?? []) as DiscountPatch[];
  const [discountPatches, setDiscountPatches] = useState<DiscountPatch[]>(
    initialDiscounts.map((d) => ({ ...d })),
  );

  const fieldOrder = Object.keys(proposedChanges);

  function updateVariant<K extends keyof VariantPatch>(
    id: number,
    key: K,
    value: VariantPatch[K] | undefined,
  ) {
    setVariantPatches((prev) =>
      prev.map((v) =>
        v.id === id
          ? value === undefined || (typeof value === "number" && Number.isNaN(value))
            ? (() => {
                const { [key]: _omit, ...rest } = v;
                void _omit;
                return { ...rest, id: v.id } as VariantPatch;
              })()
            : { ...v, [key]: value }
          : v,
      ),
    );
  }

  function updateDiscount(variantId: number, percent: number) {
    setDiscountPatches((prev) =>
      prev.map((d) => (d.variantId === variantId ? { ...d, discountPercentage: percent } : d)),
    );
  }

  async function handleSubmit() {
    // Rebuild proposedChanges by overlaying edited state onto the original
    // shape. Fields not edited (or originally absent) stay absent.
    const next: Record<string, unknown> = { ...proposedChanges };
    if ("name" in proposedChanges) next.name = name;
    if ("description" in proposedChanges) next.description = description;
    if ("categoryId" in proposedChanges) {
      const n = Number.parseInt(categoryId, 10);
      if (Number.isFinite(n)) next.categoryId = n;
    }
    if ("countryOfOriginId" in proposedChanges) {
      const n = Number.parseInt(countryOfOriginId, 10);
      if (Number.isFinite(n)) next.countryOfOriginId = n;
    }
    if ("variants" in proposedChanges) {
      // Strip patches that ended up with no fields besides `id`.
      next.variants = variantPatches.filter((v) => Object.keys(v).length > 1);
    }
    if ("discounts" in proposedChanges) {
      next.discounts = discountPatches;
    }
    await onSubmit(next);
  }

  return (
    <div className="flex flex-col gap-3">
      {fieldOrder.map((field) => {
        const label = FIELD_LABELS[field] ?? field;

        if (READ_ONLY_FIELDS.has(field)) {
          return (
            <FieldCard key={field} label={label}>
              <Text variant="bodySm" tone="muted">
                This field can't be edited here. To change it, cancel this
                request and open a new one.
              </Text>
            </FieldCard>
          );
        }

        if (field === "name") {
          return (
            <FieldCard key={field} label={label}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </FieldCard>
          );
        }

        if (field === "description") {
          return (
            <FieldCard key={field} label={label}>
              <Input
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FieldCard>
          );
        }

        if (field === "categoryId") {
          return (
            <FieldCard key={field} label={label}>
              <StyledSelect
                value={categoryId}
                onChange={setCategoryId}
                options={categoryOptions}
                placeholder="Select category"
                loading={categoriesLoading}
                searchable
              />
            </FieldCard>
          );
        }

        if (field === "countryOfOriginId") {
          return (
            <FieldCard key={field} label={label}>
              <StyledSelect
                value={countryOfOriginId}
                onChange={setCountryOfOriginId}
                options={countryOptions}
                placeholder="Select country of origin"
                loading={countriesLoading}
                searchable
              />
            </FieldCard>
          );
        }

        if (field === "variants") {
          const showPrice = variantPatches.some((v) => v.sellerPrice !== undefined);
          const showStock = variantPatches.some((v) => v.quantity !== undefined);
          return (
            <FieldCard key={field} label={label}>
              <div className="overflow-hidden rounded-xl border border-borderDefault bg-bgWhite">
                <div className="grid items-center gap-3 bg-bgLight px-3 py-2"
                  style={{
                    gridTemplateColumns: ["minmax(0,2fr)",
                      showPrice ? "minmax(0,1fr)" : null,
                      showStock ? "minmax(0,1fr)" : null,
                    ].filter(Boolean).join(" "),
                  }}>
                  <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Variant</Text>
                  {showPrice && <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Price (QAR)</Text>}
                  {showStock && <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Stock</Text>}
                </div>
                {variantPatches.map((v) => (
                  <div
                    key={v.id}
                    className="grid items-center gap-3 border-t border-borderDefault px-3 py-2"
                    style={{
                      gridTemplateColumns: ["minmax(0,2fr)",
                        showPrice ? "minmax(0,1fr)" : null,
                        showStock ? "minmax(0,1fr)" : null,
                      ].filter(Boolean).join(" "),
                    }}
                  >
                    <span className="min-w-0 truncate" title={variantLabel(v.id, productContext)}>
                      <Text variant="bodySmBold" tone="dark" inline>
                        {variantLabel(v.id, productContext)}
                      </Text>
                    </span>
                    {showPrice && (
                      <Input
                        type="number"
                        value={v.sellerPrice !== undefined ? String(v.sellerPrice) : ""}
                        onChange={(e) => {
                          const n = Number.parseFloat(e.target.value);
                          updateVariant(v.id, "sellerPrice", Number.isFinite(n) ? n : undefined);
                        }}
                      />
                    )}
                    {showStock && (
                      <Input
                        type="number"
                        value={v.quantity !== undefined ? String(v.quantity) : ""}
                        onChange={(e) => {
                          const n = Number.parseInt(e.target.value, 10);
                          updateVariant(v.id, "quantity", Number.isFinite(n) ? n : undefined);
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </FieldCard>
          );
        }

        if (field === "discounts") {
          return (
            <FieldCard key={field} label={label}>
              <div className="overflow-hidden rounded-xl border border-borderDefault bg-bgWhite">
                <div className="grid items-center gap-3 bg-bgLight px-3 py-2"
                  style={{ gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)" }}>
                  <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Variant</Text>
                  <Text variant="bodyXs" tone="muted" className="uppercase tracking-wide">Discount %</Text>
                </div>
                {discountPatches.map((d) => (
                  <div
                    key={d.variantId}
                    className="grid items-center gap-3 border-t border-borderDefault px-3 py-2"
                    style={{ gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)" }}
                  >
                    <span className="min-w-0 truncate" title={variantLabel(d.variantId, productContext)}>
                      <Text variant="bodySmBold" tone="dark" inline>
                        {variantLabel(d.variantId, productContext)}
                      </Text>
                    </span>
                    <Input
                      type="number"
                      value={String(d.discountPercentage)}
                      onChange={(e) => {
                        const n = Number.parseFloat(e.target.value);
                        updateDiscount(d.variantId, Number.isFinite(n) ? n : 0);
                      }}
                    />
                  </div>
                ))}
              </div>
            </FieldCard>
          );
        }

        return (
          <FieldCard key={field} label={label}>
            <Text variant="bodySm" tone="muted">
              {`Unsupported field "${field}" — keep current proposed value, or cancel and reopen.`}
            </Text>
          </FieldCard>
        );
      })}

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          className="rounded-full border border-borderDefault px-5 py-2 text-bodySm disabled:opacity-50"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-full bg-textDark px-5 py-2 text-bodySm text-textOnDark disabled:opacity-50"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Resubmitting…" : "Resubmit"}
        </button>
      </div>
    </div>
  );
}

function FieldCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-borderDefault bg-bgWhite px-4 py-3">
      <div className="px-1.5 mb-2">
        <Text variant="bodySmBold" inline tone="body">
          {label}
        </Text>
      </div>
      {children}
    </div>
  );
}
